import { ApolloError } from '@apollo/client';
import { Box, Stack } from '@mui/material';
import { compact, first, includes } from 'lodash';
import { useCallback, useMemo } from 'react';
import * as React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { NotFoundError } from '@/components/display/NotFoundError/NotFoundError';
import { Button } from '@/components/form/baseInputs/Button';
import { EyeIcon } from '@/components/icons/EyeIcon';
import { BigBreadcrumbs } from '@/components/navigation/BigBreadcrumbs';
import { LinkTabProps } from '@/components/navigation/NavigationTabs';
import { PageHeader } from '@/components/navigation/PageHeader';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { useModalState } from '@/hooks/useModalState';
import { useReportError } from '@/hooks/useReportError';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { DispositiveProvisionSuggestionsProvider } from '@/modules/aiDP/context/DispositiveProvisionSuggestions.provider';
import { GeneratingIndicator } from '@/modules/aiSuggestions/components/GeneratingIndicator';
import { RegenerateEntitySuggestionsV2Button } from '@/modules/aiSuggestions/components/RegenerateEntitySuggestionsV2Button';
import { useEntitySuggestionsContext } from '@/modules/aiSuggestions/context/EntitySuggestions.context';
import { EntitySuggestionsProvider } from '@/modules/aiSuggestions/context/EntitySuggestions.provider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { isBeneficiaryDesignationEntity } from '@/modules/dispositiveProvisions/dispositiveProvisions.label';
import { NoDispositionsBadge } from '@/modules/dispositiveProvisions/NoDispositionsBadge';
import { EntityActionsMenu } from '@/modules/entities/components/EntityActionsMenu/EntityActionsMenu';
import { EntityPreviewModal } from '@/modules/entities/components/EntityPreviewModal/EntityPreviewModal';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { EntityDetailsAwareRoute } from '@/modules/entities/contexts/entityDetails/EntityDetailsAwareRoute';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EditEntitySplitScreenModal } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenModal';
import { EditEntitySplitScreenOpenModalButton } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenOpenModalButton';
import { useEditEntityTrowser } from '@/modules/entities/EditEntitySplitScreen/hooks/useEditEntityTrowser';
import {
  BUSINESS_ENTITY_KINDS,
  NON_TRUST_CHARITABLE_ENTITY_KINDS,
  PERSONAL_ACCOUNT_ENTITY_KINDS,
  TRUST_ENTITY_KINDS,
} from '@/modules/entities/entities.constants';
import { useImmediateOpenEditModal } from '@/modules/entities/hooks/useImmediateOpenEditModal';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { useIsGeneratingSuggestions } from '@/modules/estate/hooks/useIsGeneratingSuggestions';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useOnHouseholdWorkflowTrackerComplete } from '@/modules/workflowTrackers/hooks/useOnHouseholdWorkflowTrackerComplete';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { SEARCH_PARAM_NAMES as MANAGE_DISPOSITIONS_PARAMS } from '@/pages/ClientDetailsPage/ClientDetailsManageDispositionsPage/ManageDispositionPage.types';
import {
  EntityKind,
  EntityStage,
  WorkflowTracker,
  WorkflowTrackerKind,
} from '@/types/schema';
import { pluralize } from '@/utils/formatting/strings';
import { getNodes } from '@/utils/graphqlUtils';

import { getBusinessDetailBadgeState } from './EntityPage.utils';
import {
  EntityPage_EntityFragment,
  useEntityPageBasicsQuery,
} from './graphql/EntityPage.generated';

function useGetEntityBasicsData(entityId: string): {
  error?: ApolloError;
  loading: boolean;
  entityData: EntityPage_EntityFragment | null;
  aiSuggestionsCount: number;
} {
  const { suggestionsByKind } = useEntitySuggestionsContext();
  const { suggestionsByKind: dpSuggestionsByKind } =
    useDispositiveProvisionSuggestionsContext();
  const { data, error, loading } = useEntityPageBasicsQuery({
    variables: { entityId },
    fetchPolicy: 'cache-and-network',
  });

  const entityData = useMemo(() => {
    if (!data || error) return null;
    return first(getNodes(data?.entities)) ?? null;
  }, [data, error]);

  const aiSuggestionsCount = useMemo(() => {
    if (!data || error) return 0;
    return [
      ...Object.values(suggestionsByKind).flat(),
      ...Object.values(dpSuggestionsByKind).flat(),
    ].filter((suggestion) => !suggestion.acceptanceStatus).length;
  }, [data, error, suggestionsByKind, dpSuggestionsByKind]);

  useReportError('failed to fetch data for entity details page', error);

  return { entityData, aiSuggestionsCount, error, loading };
}

function EntityPageInner() {
  const householdId = useRequiredParam('householdId');
  const entityId = useRequiredParam('entityId');
  const { entityNotFound } = useEntityDetailsContext();
  const { refetchSuggestions } = useEntitySuggestionsContext();
  const { refetchSuggestions: refetchDPSuggestions } =
    useDispositiveProvisionSuggestionsContext();
  const { primaryClients } = useHouseholdDetailsContext();
  const canEdit = useCanEditHouseholdData();
  const { doForceEditing, initialSection } = useImmediateOpenEditModal();
  const [
    { isModalOpen: isEntityPrintPreviewModalOpen },
    {
      openModal: openEntityPrintPreviewModal,
      closeModal: closeEntityPrintPreviewModal,
    },
  ] = useModalState();
  const navigate = useNavigate();

  const { entityData, aiSuggestionsCount } = useGetEntityBasicsData(entityId);

  const regenerateSuggestionsWorkflowTrackerFilter = useMemo(
    () => (tracker: WorkflowTracker) => {
      if (tracker.householdID !== householdId) return false;

      return Boolean(
        tracker.entities?.some((trackedEntity) => trackedEntity.id === entityId)
      );
    },
    [entityId, householdId]
  );

  const handleRegenerateSuggestionsWorkflowCompleted = useCallback(() => {
    void refetchSuggestions();
  }, [refetchSuggestions]);

  useOnHouseholdWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.AiSuggestionsV2EntityDetailsGeneration,
    enabled: Boolean(entityId),
    filter: regenerateSuggestionsWorkflowTrackerFilter,
    onCompleted: handleRegenerateSuggestionsWorkflowCompleted,
  });

  const handleRegenerateDPSuggestionsWorkflowCompleted = useCallback(() => {
    refetchDPSuggestions(entityId);
  }, [entityId, refetchDPSuggestions]);

  useOnHouseholdWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.AiSuggestionsV2DpGeneration,
    enabled: Boolean(entityId),
    filter: regenerateSuggestionsWorkflowTrackerFilter,
    onCompleted: handleRegenerateDPSuggestionsWorkflowCompleted,
  });

  const { isGeneratingSuggestions } = useIsGeneratingSuggestions(entityId, {
    detailsWorkflowTrackerKind:
      WorkflowTrackerKind.AiSuggestionsV2EntityDetailsGeneration,
  });

  const canAccessTasksPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_TASKS_PAGE
  );
  const {
    isOpenForEntity,
    section: trowserSection,
    close: closeEditEntityModal,
    openForEntity,
  } = useEditEntityTrowser();

  // Open state is URL-driven (deep-links, reload, and Back all work), so the
  // trowser simply renders when the URL names this page's entity. The entity
  // type isn't carried in the URL — derive it from the loaded entity.
  const isEditEntityModalOpen = isOpenForEntity(entityId);
  const editEntityType = entityData
    ? getEntityTypeFromEntityKind(entityData.kind)
    : undefined;

  const taskEnabledTypes = [EntityKind.GratTrust, EntityKind.IlitTrust];
  const isGrat = entityData?.kind === EntityKind.GratTrust;
  const showTasks =
    canAccessTasksPage && includes(taskEnabledTypes, entityData?.kind);

  // insurance personal accounts don't actually have assets, just insurance policies
  // so we don't show the valuation history
  const showValuationHistory =
    entityData?.kind !== EntityKind.InsurancePersonalAccount;

  // Maps entity kinds to their appropriate Estate route keys for navigation
  const getEstateRouteForEntity = (entityKind: EntityKind) => {
    if (
      TRUST_ENTITY_KINDS.includes(entityKind) ||
      NON_TRUST_CHARITABLE_ENTITY_KINDS.includes(entityKind)
    ) {
      return ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS;
    }

    if (BUSINESS_ENTITY_KINDS.includes(entityKind)) {
      return ROUTE_KEYS.HOUSEHOLD_ESTATE_BUSINESSES;
    }

    if (PERSONAL_ACCOUNT_ENTITY_KINDS.includes(entityKind)) {
      return ROUTE_KEYS.HOUSEHOLD_ESTATE_DIRECT_HOLDINGS;
    }

    // Fallback to balance sheet for unknown entity kinds
    return ROUTE_KEYS.HOUSEHOLD_SIDEBAR_ESTATE;
  };

  const trustTabs: LinkTabProps[] = React.useMemo(() => {
    return compact([
      {
        display: 'Overview',
        to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS, {
          householdId,
          entityId,
        }),
      },
      {
        display: 'Diagram',
        to: getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS_DIAGRAM,
          {
            householdId,
            entityId,
          }
        ),
      },
      showTasks && {
        display: 'Tasks',
        to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_ENTITY_TASKS, {
          householdId,
          entityId,
        }),
      },
      showValuationHistory && {
        display: 'Valuation history',
        to: getCompletePathFromRouteKey(
          isGrat
            ? ROUTE_KEYS.HOUSEHOLD_ENTITY_PERFORMANCE
            : ROUTE_KEYS.HOUSEHOLD_ENTITY_VALUATION_HISTORY,
          {
            householdId,
            entityId,
          }
        ),
      },
      isGrat && {
        display: 'Payment schedule',
        to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_ENTITY_PAYMENTS, {
          householdId,
          entityId,
        }),
      },
      {
        display: 'Documents',
        to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_ENTITY_DOCUMENTS, {
          householdId,
          entityId,
        }),
      },
    ]);
  }, [householdId, entityId, isGrat, showTasks, showValuationHistory]);

  const kind = entityData?.kind;
  const type = kind && getEntityTypeFromEntityKind(kind);
  const isBusinessEntity = kind ? BUSINESS_ENTITY_KINDS.includes(kind) : false;

  // Retirement / DH-insurance accounts edit their beneficiaries via the
  // dedicated BDG section, not the per-death-order dispositive provisions form.
  const useBeneficiaryDesignationsSection =
    isBeneficiaryDesignationEntity(kind);

  // Business entities don't natively support dispositive provisions — the
  // dispositions live on the owning client(s) — so the badge only renders
  // when the helper can name a single primary-client owner to deep-link to.
  const businessBadgeState = useMemo(() => {
    if (!isBusinessEntity) return null;
    return getBusinessDetailBadgeState(
      entityData?.ownedByStakes,
      (primaryClients ?? []).map((c) => c.id)
    );
  }, [isBusinessEntity, entityData?.ownedByStakes, primaryClients]);

  const handleAddDispositions = useCallback(() => {
    if (isBusinessEntity) {
      if (!businessBadgeState?.showBadge) return;
      // The profile to edit can be the secondary client, but that must not
      // change the household's default death order.
      const defaultFirstDeathClientId = primaryClients?.[0]?.id;
      if (!defaultFirstDeathClientId) return;
      navigate(
        getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS,
          { householdId },
          {
            [MANAGE_DISPOSITIONS_PARAMS.ACTIVE_CLIENT_DEATH_ID]:
              defaultFirstDeathClientId,
            [MANAGE_DISPOSITIONS_PARAMS.OPEN_CLIENT_PROFILE_ID]:
              businessBadgeState.targetClientProfileId,
          }
        )
      );
      return;
    }
    if (!type) return;
    const initialSection = useBeneficiaryDesignationsSection
      ? EditEntitySection.BENEFICIARY_DESIGNATIONS
      : EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1;
    openForEntity(entityId, {
      section: initialSection,
    });
  }, [
    isBusinessEntity,
    businessBadgeState,
    primaryClients,
    navigate,
    householdId,
    type,
    entityId,
    openForEntity,
    useBeneficiaryDesignationsSection,
  ]);

  const showNoDispositionsBadge = isBusinessEntity
    ? businessBadgeState?.showBadge === true
    : true;

  if (entityNotFound) {
    return (
      <Stack height="100%" justifyContent="center" alignItems="center">
        <NotFoundError
          sx={{ width: 350 }}
          title="Unable to load entity"
          message="The entity you are looking for could not be found, or you may not have the necessary access"
          buttonText="Return to client overview"
          returnPath={getCompletePathFromRouteKey(
            ROUTE_KEYS.HOUSEHOLD_DETAILS_OVERVIEW,
            { householdId }
          )}
        />
      </Stack>
    );
  }

  return (
    <Stack height={'100%'}>
      <PageHeader
        heading={
          <Stack spacing={2} direction="row" alignItems="center">
            <BigBreadcrumbs
              crumbs={[
                {
                  to: getCompletePathFromRouteKey(
                    entityData?.kind
                      ? getEstateRouteForEntity(entityData.kind)
                      : ROUTE_KEYS.HOUSEHOLD_SIDEBAR_ESTATE,
                    { householdId }
                  ),
                  label: 'Estate',
                },
                {
                  label: entityData?.subtype?.displayName ?? '',
                },
              ]}
            />
            {(entityData?.stage === EntityStage.Draft ||
              entityData?.stage === EntityStage.ReadyForProposal) && (
              <Box pb={1}>
                <Badge variant={BadgeVariants.Warning} display="Hypothetical" />
              </Box>
            )}
            {entityData?.stage === EntityStage.Completed && (
              <Box pb={1}>
                <Badge variant={BadgeVariants.Success} display="Completed" />
              </Box>
            )}
            {type && kind && showNoDispositionsBadge && (
              <NoDispositionsBadge
                entityName={entityData?.subtype?.displayName ?? ''}
                entityKind={kind}
                dispositionScenarios={entityData?.subtype?.dispositionScenarios}
                beneficiaryDesignations={
                  entityData?.subtype &&
                  'beneficiaryDesignations' in entityData.subtype
                    ? entityData.subtype.beneficiaryDesignations
                    : null
                }
                beneficiaryDesignationsReviewedAt={
                  entityData?.subtype &&
                  'beneficiaryDesignationsReviewedAt' in entityData.subtype
                    ? entityData.subtype.beneficiaryDesignationsReviewedAt
                    : null
                }
                onAddDispositions={handleAddDispositions}
                entityId={entityId}
                entitySubtypeId={entityData?.subtype?.id}
                entityType={type}
              />
            )}
          </Stack>
        }
        tabs={trustTabs}
        action={
          <Stack direction="row" spacing={1}>
            {isGeneratingSuggestions && (
              <GeneratingIndicator dataTestId="entity-page" />
            )}
            <Button
              variant="secondary"
              size="sm"
              startIcon={EyeIcon}
              iconProps={{ size: 16 }}
              onClick={() => openEntityPrintPreviewModal()}
              whiteSpace="nowrap"
            >
              Preview deliverable
            </Button>
            <EditEntitySplitScreenOpenModalButton
              buttonText={canEdit ? 'Edit entity' : 'View entity'}
              initialSection={initialSection}
              openOnMount={doForceEditing}
              whiteSpace="nowrap"
              badgeText={
                canEdit && aiSuggestionsCount
                  ? pluralize({
                      count: aiSuggestionsCount,
                      singular: 'suggestion',
                    })
                  : undefined
              }
            />
            {isEditEntityModalOpen && editEntityType && (
              <EditEntitySplitScreenModal
                entityType={editEntityType}
                initialSection={
                  trowserSection ?? EditEntitySection.BASIC_INFORMATION
                }
                isOpen
                onClose={closeEditEntityModal}
              />
            )}
            {isEntityPrintPreviewModalOpen && (
              <EntityPreviewModal
                entityId={entityId}
                onClose={closeEntityPrintPreviewModal}
                householdId={householdId}
              />
            )}
            {entityData && (
              <EntityActionsMenu
                entity={entityData}
                householdId={householdId}
                onAfterDelete={() => {
                  navigate(
                    getCompletePathFromRouteKey(
                      getEstateRouteForEntity(entityData.kind),
                      { householdId }
                    )
                  );
                }}
                additionalMenuItems={
                  // we currently show this only here rather than other places that the EntityActionsMenu
                  // is used because this RegenerateEntitySuggestionsButton requires a bunch of Providers to
                  // work correctly, and those don't exist in other consumers yet
                  <RegenerateEntitySuggestionsV2Button />
                }
              />
            )}
          </Stack>
        }
      />
      <Stack p={2} flex={1}>
        <Outlet />
      </Stack>
    </Stack>
  );
}

export function EntityPage() {
  const entityId = useRequiredParam('entityId');
  return (
    <EntityDetailsAwareRoute entityId={entityId}>
      <EntitySuggestionsProvider>
        <DispositiveProvisionSuggestionsProvider targetId={entityId}>
          <EntityPageInner />
        </DispositiveProvisionSuggestionsProvider>
      </EntitySuggestionsProvider>
    </EntityDetailsAwareRoute>
  );
}
